import { useState, useCallback } from 'react';
import { useAppDispatch } from '../redux/hooks';
import { showAlert } from '../redux/alert/alertReducer';
import printService, { PrintLabelOptions, PrintResponse } from '../services/print-service';

export type LabelPaperSize = 'A4' | 'A5' | 'SMALL_5x2_5';

// We won't change external types, just allow an optional prop when calling:
export type SizedLabelOptions = PrintLabelOptions & { labelPaperSize?: LabelPaperSize };

export interface UsePrintReturn {
  // State
  isPrinting: boolean;
  isLoading: boolean;

  // Print functions with paper size support
  printBoxLabel: (options: SizedLabelOptions) => Promise<void>;
  printMultipleBoxLabels: (labelsData: SizedLabelOptions[]) => Promise<void>;
  previewBoxLabel: (options: SizedLabelOptions) => Promise<string | null>;

  // Utility function to format box data for printing
  formatBoxDataForPrinting: (boxData: any) => SizedLabelOptions;
}

export const usePrint = (): UsePrintReturn => {
  const dispatch = useAppDispatch();
  const [isPrinting, setIsPrinting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

   const getSmallStickerStyles = useCallback(() => {
    return `
      @page {
        /* width x height */
        size: 5in 2.5in;
        margin: 0.15in; /* small margins for printers that need it */
      }
      * { box-sizing: border-box; }
      html, body {
        width: 100%;
        height: 100%;
        margin: 0;
        padding: 0;
        background: white;
      }
      body {
        display: flex;
        align-items: center;
        justify-content: center;
        font-family: Arial, sans-serif;
      }
      .sticker {
        width: 100%;
        height: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
      }
      /* Canvas/SVG area for the barcode */
      .barcode-wrap {
        width: 100%;
        height: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 0.05in 0.1in; /* tiny quiet zone */
      }
      /* Fallback text barcode style (if JsBarcode unavailable) */
      .fallback {
        font-family: "Courier New", monospace;
        font-size: 28px;
        letter-spacing: 2px;
      }
    `;
  }, []);

  const generateSmallBarcodeHTML = useCallback((options: SizedLabelOptions): string => {
    const styles = getSmallStickerStyles();
    const payload = {
      value: options.barcode || '',
      // basic JsBarcode options for dense small labels
      format: 'CODE128',
      width: 2,           // line width px
      height: 90,         // bar height px
      displayValue: false // no human-readable text (barcode only)
    };

    return `
      <!doctype html>
      <html>
        <head>
          <meta charset="utf-8" />
          <title>Label ${options.barcode || ''}</title>
          <style>${styles}</style>
          <script>
            // Try to reuse JsBarcode from the opener (parent app) if present.
            const JsBarcode = (window.opener && window.opener.JsBarcode) ? window.opener.JsBarcode : null;
            function draw(){
              const code = ${JSON.stringify(payload)};
              const c = document.getElementById('bc');
              if (JsBarcode && c) {
                try {
                  JsBarcode(c, code.value, {
                    format: code.format,
                    width: code.width,
                    height: code.height,
                    displayValue: code.displayValue,
                    margin: 6,
                    marginTop: 6,
                    marginBottom: 6,
                    marginLeft: 12,
                    marginRight: 12
                  });
                  window.print();
                  return;
                } catch(e) {}
              }
              // Fallback: show plain text barcode if JsBarcode not available.
              const fb = document.getElementById('fallback');
              if (fb) fb.style.display = 'block';
              window.print();
            }
            window.onload = draw;
          </script>
        </head>
        <body>
          <div class="sticker">
            <div class="barcode-wrap">
              <svg id="bc"></svg>
              <div id="fallback" class="fallback" style="display:none;">${options.barcode || ''}</div>
            </div>
          </div>
        </body>
      </html>
    `;
  }, [getSmallStickerStyles]);

  // Builds dynamic CSS for single-label pages based on paper size
  const getSinglePageStyles = useCallback((size: LabelPaperSize) => {
    // A4: 210x297mm, A5: 148x210mm
    const page = size === 'A5' ? 'A5' : 'A4';

    // Tune label container sizing for each paper
    const labelTuning =
      size === 'A5'
        ? `
          .label-container { max-width: 350px; padding: 14px; }
          .title { font-size: 18px; }
          .barcode { font-size: 36px; }
          .barcode-visual { height: 54px; }
          .barcode-text { font-size: 13px; }
        `
        : `
          .label-container { max-width: 420px; padding: 16px; }
          .title { font-size: 20px; }
          .barcode { font-size: 40px; }
          .barcode-visual { height: 60px; }
          .barcode-text { font-size: 14px; }
        `;

    return `
      @page {
        size: ${page};
        margin: 12mm;
      }

      * {
        margin: 0;
        padding: 0;
        box-sizing: border-box;
      }

      body {
        font-family: 'Courier New', monospace;
        background: white;
        color: black;
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        min-height: calc(100vh - 24mm);
        padding: 10px;
      }

      .label-container {
        border: 2px solid black;
        width: 100%;
        text-align: center;
        background: white;
      }

      .title {
        font-weight: bold;
        margin-bottom: 10px;
        text-transform: uppercase;
        border-bottom: 1px solid black;
        padding-bottom: 5px;
      }

      .barcode {
        font-family: 'Libre Barcode 128', 'Courier New', monospace;
        margin: 15px 0;
        letter-spacing: 2px;
        word-break: break-all;
        line-height: 1.2;
      }

      .barcode-text {
        font-weight: bold;
        margin-bottom: 15px;
        background: #f0f0f0;
        padding: 5px;
        border-radius: 3px;
      }

      .info-row {
        display: flex;
        justify-content: space-between;
        margin: 8px 0;
        font-size: 12px;
        border-bottom: 1px dotted #ccc;
        padding-bottom: 3px;
      }

      .info-label { font-weight: bold; }
      .info-value { text-align: right; }

      .warehouse-info {
        background: #e8f4fd;
        padding: 8px;
        margin: 10px 0;
        border-radius: 4px;
        font-size: 11px;
      }

      .print-button {
        position: fixed;
        top: 20px;
        right: 20px;
        background: #2196f3;
        color: white;
        border: none;
        padding: 10px 20px;
        border-radius: 5px;
        cursor: pointer;
        font-size: 14px;
        box-shadow: 0 2px 5px rgba(0,0,0,0.2);
      }

      .print-button:hover { background: #1976d2; }

      @media print {
        .print-button { display: none; }
        body { margin: 0; padding: 0; }
        .label-container { border: 3px solid black; margin: 0; width: 100%; max-width: none; }
      }

      .barcode-visual {
        background: repeating-linear-gradient(
          90deg,
          black,
          black 1px,
          white 1px,
          white 3px
        );
        margin: 10px 0;
        position: relative;
      }

      .barcode-visual::before {
        content: '';
        position: absolute;
        top: 0; left: 0; right: 0; bottom: 0;
        background: repeating-linear-gradient(
          90deg,
          transparent 0px,
          transparent 1px,
          black 1px,
          black 2px,
          transparent 2px,
          transparent 4px,
          black 4px,
          black 5px
        );
      }

      ${labelTuning}
    `;
  }, []);

  const generateBarcodeHTML = useCallback((options: SizedLabelOptions): string => {
     const paperSize: LabelPaperSize = options.labelPaperSize ?? 'A4';
    if (paperSize === 'SMALL_5x2_5') {
      // Special minimal sticker layout (barcode-only)
      return generateSmallBarcodeHTML(options);
    }
    const styles = getSinglePageStyles(paperSize);

    return `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <title>Box Label - ${options.barcode}</title>
        <style>${styles}</style>
        <script>
          function printLabel() {
            window.print();
          }

          window.addEventListener('load', function() {
            const btn = document.querySelector('.print-button');
            if (btn) btn.focus();
          });

          document.addEventListener('keydown', function(e) {
            if (e.ctrlKey && e.key === 'p') {
              e.preventDefault();
              printLabel();
            }
            if (e.key === 'Escape') {
              window.close();
            }
          });
        </script>
      </head>
      <body>
        <button class="print-button" onclick="printLabel()">🖨️ Print Label (Ctrl+P)</button>

        <div class="label-container">
          <div class="title">Box Label</div>

          <div class="barcode-visual"></div>

          <div class="barcode">${options.barcode}</div>
          <div class="barcode-text">${options.barcode}</div>

          <div class="warehouse-info">
            <strong>${options.warehouseName || 'N/A'}</strong>
            ${options.storageUnitName ? `<br>Unit: ${options.storageUnitName}` : ''}
          </div>

          <div class="info-row">
            <span class="info-label">Printed:</span>
            <span class="info-value">${new Date().toLocaleString()}</span>
          </div>
        </div>
      </body>
      </html>
    `;
  }, [getSinglePageStyles, generateSmallBarcodeHTML]);
 

  const printBoxLabel = useCallback(async (options: SizedLabelOptions) => {
    setIsPrinting(true);
    try {
      const htmlContent = generateBarcodeHTML(options);
      const w = window.open('', '_blank', 'width=900,height=1000,scrollbars=yes,resizable=yes');

      if (!w) {
        throw new Error('Could not open print window. Please check your popup blocker settings.');
      }

      w.document.write(htmlContent);
      w.document.close();
      w.focus();

      dispatch(showAlert({
        message: `Print preview opened for box ${options.barcode}`,
        type: 'success'
      }));
    } catch (error: any) {
      dispatch(showAlert({
        message: error.message || 'Failed to open print preview',
        type: 'error'
      }));
    } finally {
      setIsPrinting(false);
    }
  }, [dispatch, generateBarcodeHTML]);

  const printMultipleBoxLabels = useCallback(async (labelsData: SizedLabelOptions[]) => {
    setIsPrinting(true);
    try {
      const sizeFromFirst: LabelPaperSize = labelsData[0]?.labelPaperSize ?? 'A4';
      const page = sizeFromFirst === 'A5' ? 'A5' : 'A4';

      // Layout tweaks: 2 columns on A4, 1 column on A5 for larger, clearer labels.
      const gridCols = sizeFromFirst === 'A5' ? 1 : 2;

      const multiLabelHTML = `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="utf-8">
          <title>Multiple Box Labels</title>
          <style>
            @page {
              size: ${page};
              margin: 12mm;
            }

            body {
              font-family: 'Courier New', monospace;
              margin: 0;
              padding: 12mm;
            }

            .print-button {
              position: fixed;
              top: 20px;
              right: 20px;
              background: #2196f3;
              color: white;
              border: none;
              padding: 10px 20px;
              border-radius: 5px;
              cursor: pointer;
              z-index: 9999;
            }

            @media print { .print-button { display: none; } }

            .labels-grid {
              display: grid;
              grid-template-columns: repeat(${gridCols}, 1fr);
              gap: 16px;
            }

            .label-item {
              border: 2px solid black;
              padding: 12px;
              text-align: center;
              break-inside: avoid;
              page-break-inside: avoid;
              background: white;
            }

            .title {
              font-size: 14px;
              font-weight: bold;
              margin-bottom: 8px;
              border-bottom: 1px solid black;
              padding-bottom: 4px;
              text-transform: uppercase;
            }

            .barcode {
              font-size: 28px;
              margin: 12px 0 6px 0;
              font-weight: bold;
              letter-spacing: 2px;
              line-height: 1.2;
              word-break: break-all;
            }

            .barcode-visual {
              background: repeating-linear-gradient(
                90deg,
                black,
                black 1px,
                white 1px,
                white 3px
              );
              height: 46px;
              margin: 8px 0;
              position: relative;
            }

            .barcode-visual::before {
              content: '';
              position: absolute;
              top: 0; left: 0; right: 0; bottom: 0;
              background: repeating-linear-gradient(
                90deg,
                transparent 0px,
                transparent 1px,
                black 1px,
                black 2px,
                transparent 2px,
                transparent 4px,
                black 4px,
                black 5px
              );
            }

            .info {
              font-size: 11px;
              margin: 4px 0;
            }

            .muted {
              color: #666;
              font-size: 10px;
              margin-top: 4px;
            }
          </style>
          <script>
            function printLabels() {
              window.print();
            }
          </script>
        </head>
        <body>
          <button class="print-button" onclick="printLabels()">🖨️ Print All Labels</button>
          <div class="labels-grid">
            ${labelsData.map(label => `
              <div class="label-item">
                <div class="title">Box Label</div>
                <div class="barcode-visual"></div>
                <div class="barcode">${label.barcode}</div>
                <div class="info"><strong>Warehouse:</strong> ${label.warehouseName || 'N/A'}</div>
                <div class="info"><strong>Unit:</strong> ${label.storageUnitName || 'N/A'}</div>
                <div class="muted">Printed: ${new Date().toLocaleString()}</div>
              </div>
            `).join('')}
          </div>
        </body>
        </html>
      `;

      const w = window.open('', '_blank', 'width=1000,height=1200,scrollbars=yes,resizable=yes');

      if (!w) {
        throw new Error('Could not open print window. Please check your popup blocker settings.');
      }

      w.document.write(multiLabelHTML);
      w.document.close();
      w.focus();

      dispatch(showAlert({
        message: `Print preview opened for ${labelsData.length} box labels`,
        type: 'success'
      }));
    } catch (error: any) {
      dispatch(showAlert({
        message: error.message || 'Failed to open print preview for multiple labels',
        type: 'error'
      }));
    } finally {
      setIsPrinting(false);
    }
  }, [dispatch]);

  const previewBoxLabel = useCallback(async (options: SizedLabelOptions): Promise<string | null> => {
    setIsLoading(true);
    try {
      const htmlContent = generateBarcodeHTML(options);
      return htmlContent;
    } catch (error: any) {
      dispatch(showAlert({
        message: error.message || 'Failed to generate label preview',
        type: 'error'
      }));
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [dispatch, generateBarcodeHTML]);

  // Formats box data for printing with paper size support
  // Defaults to A4 paper size if not specified
  const formatBoxDataForPrinting = useCallback((boxData: any): SizedLabelOptions => {
    return {
      barcode: boxData.barcode || '',
      warehouseName: boxData.warehouseName || '',
      storageUnitName: boxData.storageUnitName || '',
      totalProducts: boxData.totalProducts || 0,
      totalQuantities: boxData.totalQuantities || 0,
      // default to A4 unless caller overrides
      labelPaperSize: (boxData.labelPaperSize as LabelPaperSize) || 'SMALL_5x2_5',
      ...boxData
    };
  }, []);

  return {
    isPrinting,
    isLoading,
    printBoxLabel,
    printMultipleBoxLabels,
    previewBoxLabel,
    formatBoxDataForPrinting
  };
};
